import './LiveViewer.sass';

import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LiveMediaStream } from '$core/Live/LiveMediaStream/LiveMediaStream';
import { BottomBar, Button } from '$uikit';
import { useDispatch, useSelector } from 'react-redux';
import { liveActions, liveSelector } from '$store/live';
import { LivePermissionEnum, LiveStatus } from '$store/live/interface';
import { LiveHeader } from '$core/Live/LiveHeader/LiveHeader';
import { useTranslator, useUnitName, useUserInfo } from '$hooks';
import { goTo, hideModal } from '$navigation/helper';
import { ReactComponent as IcErotic28 } from '../LiveTypes/assets/erotic.svg';
import { ReactComponent as IcSponsors28 } from '../LiveTypes/assets/sponsors.svg';
import { FEED, SUBSCRIPTION_ADULT, SUBSCRIPTION_AUTHOR } from '$shared/constants/pages';
import { useRoute, useRouter } from 'react-router5';
import { authSelector } from '$store/auth';
import {
  IcBan28,
  IcLogo40,
  IcPlay28,
  IcPlay48,
  IcShare24,
  IcSoundOff24,
  IcSoundOn24,
} from '$assets';
import { LivePrompt } from '$core/Live/LivePrompt/LivePrompt';
import { LiveHistory } from '$core/Live/LiveHistory/LiveHistory';
import { setStorageItem, statReachGoal } from '$utils';
import { AttachLink, CurrencyEnum } from '$store/models';
import { mainActions } from '$store/main';
import { ShareSheet } from '$shared/components';
import { balanceActions } from '$store/balance';

export const LiveViewer: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { isLogged } = useSelector(authSelector);
  const { liveStatus, stream, online, reconnecting, tipsTotal, focus } =
    useSelector(liveSelector);
  const { route, router } = useRoute();
  const user = useUserInfo(stream?.streamerId || 0);
  const userNameGenitive = useUnitName(user, 'genitive');
  const initMuted = !route.meta?.options?.click;
  const [muted, setMuted] = useState(initMuted);
  const [showTime, setShowTime] = useState(false);
  const showTimeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [showMutedOverlay, setShowMutedOverlay] = useState(isLogged && initMuted);

  useEffect(() => {
    statReachGoal('live_viewer_view', {
      userId: user?.id,
    });
  }, [user?.id]);

  const handleClose = useCallback(() => {
    dispatch(liveActions.stopWatchLive());
    hideModal();
  }, [dispatch]);

  const handleLogin = () => {
    setStorageItem('afterRegAction', 'live_' + stream?.id);
    router.navigate(FEED);
  };

  const handleEroticSubscription = useCallback(() => {
    goTo(SUBSCRIPTION_ADULT, {
      target: 'live',
      target_model: user.id,
    });
  }, [user]);

  const handleShare = useCallback(() => {
    const link: AttachLink = {
      type: 'live',
      object: user.domain || 'id' + user.id,
    };

    dispatch(
      mainActions.setPopup(
        <ShareSheet title={t('live_share_title')} link={link} disableInternalShare />,
      ),
    );

    statReachGoal('live_share', {
      liveId: stream?.id,
      streamerId: stream?.streamerId,
    });
  }, [dispatch, stream?.id, stream?.streamerId, t]);

  const handleSponsorSubscription = useCallback(() => {
    router.navigate(SUBSCRIPTION_AUTHOR, { authorId: user?.id });
  }, [router, user?.id]);

  const toggleMute = useCallback(() => {
    setMuted(!muted);
    setShowMutedOverlay(false);
    const video = document.querySelector('.LiveViewer video') as HTMLVideoElement;
    if (video) {
      video.muted = !muted;
    }
  }, [muted]);

  const [message, bottom]: [ReactNode?, ReactNode?] = useMemo(() => {
    if (!isLogged) {
      return [
        <>
          <div className="LiveViewer__message">
            <div className="LiveViewer__message__icon">
              <IcLogo40 />
            </div>
            {t('live_not_login_title')}
          </div>
        </>,
        <>
          <Button stretched onClick={handleLogin}>
            {t('live_not_login_button')}
          </Button>
        </>,
      ];
    } else if (reconnecting) {
      return [<>{t('live_reconnecting')}</>];
    } else if (liveStatus === LiveStatus.BANNED) {
      return [
        <>
          <div className="LiveViewer__message">
            <div className="LiveViewer__message__icon banned">
              <IcBan28 />
            </div>
            {t('live_banned_title')}
          </div>
        </>,
        <>
          <Button stretched onClick={handleClose}>
            {t('live_finished_button')}
          </Button>
        </>,
      ];
    } else if (liveStatus === LiveStatus.FINISHED) {
      return [
        <>
          <div className="LiveViewer__message">
            <div className="LiveViewer__message__icon">
              <IcBan28 />
            </div>
            {t('live_finished_title')}
          </div>
        </>,
        <>
          <Button stretched onClick={handleClose}>
            {t('live_finished_button')}
          </Button>
        </>,
      ];
    } else if (liveStatus === LiveStatus.NO_ACCESS) {
      switch (stream?.type) {
        case LivePermissionEnum.SPONSOR:
          return [
            <>
              <div className="LiveViewer__message">
                <div className="LiveViewer__message__icon">
                  <IcSponsors28 />
                </div>
                {t('live_no_access_sponsor_title', { name: userNameGenitive })}
              </div>
            </>,
            <>
              <Button stretched fillColor="red" onClick={handleSponsorSubscription}>
                {t('live_no_access_sponsor_button')}
              </Button>
            </>,
          ];
        case LivePermissionEnum.EROTIC:
          return [
            <>
              <div className="LiveViewer__message">
                <div className="LiveViewer__message__icon">
                  <IcErotic28 />
                </div>
                {t('live_no_access_erotic_title')}
              </div>
            </>,
            <>
              <Button stretched fillColor="purple" onClick={handleEroticSubscription}>
                {t('live_no_access_erotic_button')}
              </Button>
            </>,
          ];
        default:
          return [
            <>Live error</>,
            <>
              <Button onClick={handleClose}>Close</Button>
            </>,
          ];
      }
    } else {
      return [];
    }
  }, [liveStatus, isLogged, user, muted, toggleMute, reconnecting]);

  const handleSendTips = useCallback(() => {
    stream?.streamerId &&
      dispatch(
        balanceActions.setTipsData({
          peerId: stream.streamerId,
          currency: CurrencyEnum.EUR,
          liveId: stream.id,
        }),
      );
  }, [stream]);

  const handleShowTime = useCallback(() => {
    showTimeTimeout.current && clearTimeout(showTimeTimeout.current);
    setShowTime(!showTime);
    showTimeTimeout.current = setTimeout(() => {
      setShowTime(false);
    }, 3000);
  }, [showTime]);

  return (
    <div className="LiveViewer">
      <LiveHeader
        showControls={!message && liveStatus === LiveStatus.ONLINE}
        tips={tipsTotal}
        onTipsClick={handleSendTips}
        createdAt={stream?.createdAt}
        onTimeClick={handleShowTime}
        user={user}
        liveLabel={!showTime}
        viewers={online}
        userLink={true}
        onClose={handleClose}
      />
      <div className="LiveViewer__body">
        {showMutedOverlay && liveStatus === LiveStatus.ONLINE && (
          <div className="LiveViewer__overlay" onClick={toggleMute}>
            <div className="LiveViewer__overlay__play">
              <IcSoundOn24 />
            </div>
            <div className="LiveViewer__overlay__tip">
              {t('live_to_turn_on_turn_sound')}
            </div>
          </div>
        )}
        <LiveMediaStream
          muted={initMuted}
          cover={user?.photo}
          content={!(showMutedOverlay && liveStatus === LiveStatus.ONLINE) && message}
        />
        {liveStatus === LiveStatus.ONLINE && (
          <LiveHistory
            onMsgClick={(m, user) => {
              window.open(`/${user.domain || 'id' + user.id} `, '_blank');
            }}
          />
        )}
        {liveStatus === LiveStatus.ONLINE && !message && !showMutedOverlay && (
          <div className="LivePublisher__controls">
            <div className="LivePublisher__controls__btn" onClick={toggleMute}>
              {!muted ? <IcSoundOn24 /> : <IcSoundOff24 />}
            </div>
            <div className="LivePublisher__controls__btn" onClick={handleShare}>
              <IcShare24 />
            </div>
          </div>
        )}
      </div>
      <BottomBar hasSafeArea={!focus}>
        {bottom || <LivePrompt disabled={showMutedOverlay} tips={true} />}
      </BottomBar>
    </div>
  );
};
